/**
 * Step 3 — module picker rows. Source + active/locked logic mirror ModulePicker:
 * only `active` modules are selectable; `binnenkort` rows render disabled with a
 * "binnenkort" tag. Module labels come from MODULE_DEFINITIONS (DRY — no
 * duplicated labels); subtitles from onboarding.oefenen.sub.<slug>.
 */
import {
  MODULE_DEFINITIONS,
  MODULE_SLUGS,
  type ModuleSlug,
  type ModuleStatusMap,
} from "@a2/core";
import { useT } from "../../../lib/use-t";

function ModuleIcon({ slug }: { slug: ModuleSlug }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (slug === "spreken")
    return (
      <svg {...common}>
        <path d="M11 5L6 9H2v6h4l5 4zM15.5 8.5a5 5 0 010 7M19 5a9 9 0 010 14" />
      </svg>
    );
  if (slug === "schrijven")
    return (
      <svg {...common}>
        <path d="M12 19l7-7 3 3-7 7-3-3zM2 22l5-1 11-11-4-4L3 17z" />
      </svg>
    );
  // lezen / luisteren / knm — book glyph
  return (
    <svg {...common}>
      <path d="M2 5a2 2 0 012-2h6a2 2 0 012 2v14M22 5a2 2 0 00-2-2h-6a2 2 0 00-2 2v14" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 6" />
    </svg>
  );
}

export function StepOefenen({
  statusMap,
  value,
  onChange,
}: {
  statusMap: ModuleStatusMap;
  value: ModuleSlug;
  onChange: (m: ModuleSlug) => void;
}) {
  const t = useT();
  const rows = MODULE_SLUGS.filter((slug) => statusMap[slug] !== "hidden");

  return (
    <div className="flex flex-col gap-3">
      {rows.map((slug) => {
        const active = statusMap[slug] === "active";
        const selected = value === slug && active;
        return (
          <button
            key={slug}
            type="button"
            disabled={!active}
            aria-pressed={active ? selected : undefined}
            onClick={() => active && onChange(slug)}
            className={[
              "flex items-center gap-3.5 rounded-[var(--radius)] border-2 px-[18px] py-4 text-left transition-[transform,border-color,background-color] duration-150 ease-out",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2",
              active
                ? "cursor-pointer hover:-translate-y-px"
                : "cursor-not-allowed opacity-50",
              selected
                ? "border-[var(--accent)] bg-[var(--accent-soft)]"
                : "border-[var(--border)] bg-[var(--panel)] hover:border-[var(--border-strong)]",
            ].join(" ")}
          >
            <span
              className={[
                "grid h-10 w-10 flex-none place-items-center rounded-[11px]",
                selected ? "bg-[var(--accent)] text-white" : "bg-[var(--panel-soft)] text-[var(--ink-muted)]",
              ].join(" ")}
            >
              <ModuleIcon slug={slug} />
            </span>
            <span className="min-w-0">
              <span className="block text-[15.5px] font-semibold text-[var(--ink)]">
                {MODULE_DEFINITIONS[slug].label}
              </span>
              <span className="block text-[13px] text-[var(--ink-muted)]">
                {t(`onboarding.oefenen.sub.${slug}`)}
              </span>
            </span>
            {!active ? (
              <span className="ml-auto font-mono-badge text-[9px] uppercase tracking-wide text-[var(--ink-faint)]">
                {t("onboarding.oefenen.comingSoon")}
              </span>
            ) : slug === "spreken" ? (
              <span className="ml-auto rounded-full bg-[var(--accent-soft)] px-2.5 py-1 font-mono-badge text-[9px] uppercase tracking-wide text-[var(--accent)]">
                {t("onboarding.oefenen.recommended")}
              </span>
            ) : null}
            {selected ? (
              <span className="ml-2 flex-none text-[var(--accent)]">
                <CheckGlyph />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
